"use client";

/**
 * fund-tax-info.tsx
 *
 * Tax implications cards — adapts rates based on fund type (Equity/ELSS/
 * Hybrid-equity → equity tax rules; Debt/Liquid/Money Market → slab rate
 * post-Finance-Act-2023). Adds an amber 3-year lock-in badge for ELSS and
 * a SIP tax tip callout. Visible on ALL tiers — never paywalled.
 *
 * Used in: src/app/funds/[slug]/page.tsx (after News & Changes section)
 */

import { Receipt } from "lucide-react";

export interface FundTaxInfoProps {
  fundType: string; // "Equity" | "Debt" | "Hybrid" | "ELSS" | "Index" | etc.
  isElss: boolean;
}

interface TaxCard {
  label: string;
  rate: string;
  subtitle: string;
  borderColor: string;
  labelColor: string;
}

function getTaxCards(fundType: string, isElss: boolean): {
  stcg: TaxCard;
  ltcg: TaxCard;
  isEquityTreatment: boolean;
} {
  const type = fundType.toLowerCase();
  const isDebt =
    type.includes("debt") ||
    type.includes("liquid") ||
    type.includes("money market") ||
    type.includes("gilt") ||
    type.includes("bond") ||
    type.includes("duration");

  if (isDebt) {
    return {
      isEquityTreatment: false,
      stcg: {
        label: "STCG",
        rate: "Slab rate",
        subtitle: "Taxed at your income tax slab (post Finance Act 2023)",
        borderColor: "#EF4444",
        labelColor: "#EF4444",
      },
      ltcg: {
        label: "LTCG",
        rate: "Slab rate",
        subtitle: "No indexation benefit: slab rate applies regardless of holding period",
        borderColor: "#EF4444",
        labelColor: "#EF4444",
      },
    };
  }

  // Equity, ELSS, Index (equity), Hybrid (equity-oriented ≥65% equity) all use equity tax rules
  return {
    isEquityTreatment: true,
    stcg: {
      label: "STCG",
      rate: "20%",
      subtitle: "If sold within 1 year of purchase",
      borderColor: "#EF4444",
      labelColor: "#EF4444",
    },
    ltcg: {
      label: "LTCG",
      rate: "12.5%",
      subtitle: isElss
        ? "Applies after 3-year lock-in. First ₹1.25L profit/year is tax-free."
        : "If held over 1 year. First ₹1.25L profit/year is tax-free.",
      borderColor: "#10B981",
      labelColor: "#10B981",
    },
  };
}

function TaxCardTile({
  card,
  badge,
}: {
  card: TaxCard;
  badge?: string | null;
}) {
  return (
    <div
      className="relative bg-surface rounded-lg p-4 border-l-[3px]"
      style={{ borderLeftColor: card.borderColor }}
    >
      <div
        className="text-[11px] font-bold uppercase tracking-wide mb-2"
        style={{ color: card.labelColor }}
      >
        {card.label}
      </div>
      <div className="text-foreground text-[20px] font-bold leading-none mb-1.5">
        {card.rate}
      </div>
      <div className="text-muted-foreground text-xs">{card.subtitle}</div>
      {badge && (
        <span className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gold/15 text-gold">
          {badge}
        </span>
      )}
    </div>
  );
}

export function FundTaxInfo({ fundType, isElss }: FundTaxInfoProps) {
  const { stcg, ltcg } = getTaxCards(fundType, isElss);

  return (
    <section className="mt-6 space-y-4">
      <div className="flex items-center gap-2">
        <Receipt className="w-4 h-4 text-gold" />
        <h2 className="text-foreground text-base font-serif">Tax implications</h2>
      </div>

      {/* Two cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TaxCardTile card={stcg} />
        <TaxCardTile card={ltcg} badge={isElss ? "3-year lock-in" : null} />
      </div>

      {/* SIP tax tip callout */}
      <div
        className="rounded-lg p-4 border-l-[3px]"
        style={{
          backgroundColor: "rgba(245,158,11,0.06)",
          borderLeftColor: "#F59E0B",
        }}
      >
        <div className="text-gold text-xs font-bold mb-1">SIP tax tip</div>
        <div className="text-muted-foreground text-xs leading-relaxed">
          Each SIP installment has its own 1-year clock. Your Jan 2025 SIP
          becomes long-term in Jan 2026. Your Feb 2025 SIP in Feb 2026. Each
          month is treated as a separate purchase for tax purposes.
        </div>
      </div>

      <div className="text-dim text-[11px]">
        Stamp duty: 0.005% on all purchases (₹5 per ₹1 lakh). Negligible.
      </div>
    </section>
  );
}
